import { createTheme } from '@mui/material';
import { colors } from '../utils/colors';
import { bluePalette } from './bluePalette';
import { layoutTokens, sharedDataGridRootStyle, sharedNonColorComponents } from './layoutTokens';
import { getEasterComponentOverrides } from './easterTheme';

const easterComponentOverrides = getEasterComponentOverrides(true);

export const blueTheme = createTheme({
    typography: {
        fontFamily: 'Source Sans Pro, -apple-system, Segoe UI, Helvetica Neue, sans-serif'
    },
    shape: {
        borderRadius: layoutTokens.borderRadiusBase
    },
    palette: {
        ...bluePalette,
        primary: { main: colors.blue.i2x_flashy },
        secondary: { main: colors.blue.i2x_flashy },
        text: {
            primary: colors.white.basic,
            secondary: bluePalette.betaTagColor
        },
        gray: { main: colors.gray.cold_gray }
    },
    components: {
        ...sharedNonColorComponents,
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: bluePalette.contentPaperText
                },
                colorPrimary: {
                    color: bluePalette.contentPaperText
                }
            }
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    minHeight: layoutTokens.filterControlHeight,
                    fontSize: `${layoutTokens.controlFontSize}px`,
                    boxSizing: 'border-box',
                    border: `1px solid ${bluePalette.filterBorderColor}`,
                    borderRadius: bluePalette.contentFilterInputBorderRadius || '4px',
                    backgroundColor: bluePalette.contentFilterInputBackground,
                    padding: bluePalette.contentFilterInputPadding || '7px 8px',
                    paddingRight: 32,
                    '&:before, &:after': {
                        borderBottom: 'none !important'
                    },
                    '&:hover': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '&.Mui-focused': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiSelect-select': {
                        minHeight: '20px !important',
                        fontSize: `${layoutTokens.controlFontSize}px`,
                        display: 'flex',
                        alignItems: 'center'
                    },
                    '& .MuiSelect-icon': {
                        right: 7
                    }
                }
            }
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small'
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    minHeight: layoutTokens.filterControlHeight,
                    color: bluePalette.dateRangeText,
                    backgroundColor: bluePalette.contentFilterInputBackground,
                    borderRadius: bluePalette.contentFilterInputBorderRadius || `${layoutTokens.borderRadiusControl}px`,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.borderColor
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '&.Mui-disabled': {
                        backgroundColor: bluePalette.contentFilterInputBackground
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.filterBorderColor
                    },
                    '& .MuiSvgIcon-root': {
                        color: bluePalette.downArrowColor
                    }
                },
                input: {
                    fontSize: layoutTokens.controlFontSize,
                    '&.Mui-disabled': {
                        WebkitTextFillColor: bluePalette.dateRangeText,
                        color: bluePalette.dateRangeText,
                        opacity: 0.75
                    }
                },
                inputAdornedEnd: {
                    color: bluePalette.dateRangeText
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: bluePalette.dateRangeText,
                    '& .MuiSvgIcon-root': {
                        color: bluePalette.downArrowColor
                    }
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        minHeight: layoutTokens.filterControlHeight,
                        backgroundColor: bluePalette.contentFilterInputBackground,
                        borderRadius:
                            bluePalette.contentFilterInputBorderRadius || `${layoutTokens.borderRadiusControl}px`,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: bluePalette.filterBorderColor
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: bluePalette.dateRangeLabelHighlighted
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: bluePalette.dateRangeLabelHighlighted
                        }
                    },
                    '& .MuiInputBase-root': {
                        color: bluePalette.contentPaperText
                    },
                    '& .MuiInputBase-input': {
                        color: bluePalette.contentPaperText,
                        WebkitTextFillColor: bluePalette.contentPaperText
                    },
                    '& .MuiInputLabel-root': {
                        color: bluePalette.inputLabelColor
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: bluePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiInputBase-root.MuiInput-root': {
                        minHeight: layoutTokens.filterControlHeight,
                        boxSizing: 'border-box',
                        border: `1px solid ${bluePalette.filterBorderColor}`,
                        borderRadius: bluePalette.contentFilterInputBorderRadius || '4px',
                        backgroundColor: bluePalette.contentFilterInputBackground,
                        padding: bluePalette.contentFilterInputPadding || '7px 8px',
                        paddingRight: 32,
                        color: bluePalette.contentPaperText,
                        '&:before, &:after': {
                            borderBottom: 'none !important'
                        }
                    },
                    '& .MuiSelect-select': {
                        minHeight: '20px !important',
                        display: 'flex',
                        alignItems: 'center'
                    },
                    '& .MuiAutocomplete-inputRoot': {
                        minHeight: layoutTokens.filterControlHeight
                    },
                    '& .MuiSvgIcon-root': {
                        color: bluePalette.downArrowColor
                    }
                },
                paper: {
                    backgroundColor: bluePalette.dropdownMenuPaperBg,
                    color: bluePalette.dateRangeText,
                    border: `1px solid ${bluePalette.filterPopupBorder}`,
                    borderRadius: `${layoutTokens.borderRadiusControl}px`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                },
                listbox: {
                    backgroundColor: 'transparent'
                },
                option: {
                    color: bluePalette.contentPaperText,
                    '&[aria-selected="true"]': {
                        backgroundColor: bluePalette.dropdownMenuItemSelectedBg
                    },
                    '&.Mui-focused': {
                        backgroundColor: bluePalette.dropdownMenuItemSelectedHoverBg
                    }
                },
                groupLabel: {
                    backgroundColor: 'transparent',
                    color: bluePalette.contentPaperText
                },
                popupIndicator: {
                    color: bluePalette.contentPaperText
                },
                clearIndicator: {
                    color: bluePalette.contentPaperText
                }
            }
        },
        MuiPopper: {
            styleOverrides: {
                root: {
                    zIndex: 2000
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: bluePalette.dropdownMenuPaperBg,
                    color: bluePalette.contentPaperText,
                    border: `1px solid ${bluePalette.filterPopupBorder}`,
                    borderRadius: `${layoutTokens.borderRadiusControl}px`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                },
                list: {
                    backgroundColor: 'transparent'
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: `${layoutTokens.controlFontSize}px`,
                    color: bluePalette.contentPaperText,
                    '&:hover': {
                        backgroundColor: bluePalette.dropdownMenuItemSelectedHoverBg
                    },
                    '&.Mui-selected': {
                        backgroundColor: bluePalette.dropdownMenuItemSelectedBg
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: bluePalette.dropdownMenuItemSelectedHoverBg
                    }
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiBackdrop-root': {
                        backgroundColor: bluePalette.modalBackdropBg
                    }
                },
                paper: {
                    borderRadius: layoutTokens.borderRadiusPanel,
                    padding: 0,
                    backdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    WebkitBackdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    background: bluePalette.contentPaperBackground,
                    color: bluePalette.contentPaperText,
                    border: `1px solid ${bluePalette.contentPaperBorder}`,
                    '& .MuiDialogTitle-root': {
                        color: bluePalette.contentPaperText
                    },
                    '& .MuiDialogContent-root': {
                        color: bluePalette.contentPaperText
                    },
                    '& .MuiDialogContentText-root': {
                        color: bluePalette.contentPaperText
                    },
                    '& .MuiInputBase-root': {
                        color: bluePalette.dateRangeText
                    },
                    '& .MuiInputLabel-root': {
                        color: bluePalette.inputLabelColor
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: bluePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.borderColor
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiFormControl-root .MuiSvgIcon-root': {
                        color: bluePalette.downArrowColor
                    },
                    '& .MuiButton-root': {
                        borderRadius: layoutTokens.borderRadiusControl
                    },
                    '& .MuiButton-text:not(.MuiButton-colorError)': {
                        color: bluePalette.buttonAccentBg,
                        '&:hover': {
                            backgroundColor: bluePalette.buttonAccentSoftBg
                        }
                    },
                    '& .MuiButton-outlined:not(.MuiButton-colorError)': {
                        color: bluePalette.buttonAccentBg,
                        borderColor: bluePalette.buttonAccentBg,
                        backgroundColor: 'transparent',
                        '&:hover': {
                            borderColor: bluePalette.buttonAccentBg,
                            backgroundColor: bluePalette.buttonAccentSoftBg
                        },
                        '&.Mui-disabled': {
                            color: bluePalette.buttonAccentDisabledText,
                            borderColor: bluePalette.buttonAccentDisabledBorder,
                            backgroundColor: bluePalette.surfaceOverlayHover
                        }
                    },
                    '& .evaluation-view-close-btn.MuiButton-outlined': {
                        color: `${bluePalette.buttonAccentBg} !important`,
                        borderColor: `${bluePalette.buttonAccentBg} !important`,
                        backgroundColor: 'transparent !important',
                        '&:hover': {
                            borderColor: `${bluePalette.buttonAccentBg} !important`,
                            backgroundColor: `${bluePalette.buttonAccentSoftActiveBg} !important`
                        }
                    },
                    '& .MuiButton-contained:not(.MuiButton-colorError)': {
                        color: bluePalette.buttonAccentText,
                        backgroundColor: bluePalette.buttonAccentBg,
                        borderColor: bluePalette.buttonAccentBg,
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                            backgroundColor: bluePalette.buttonAccentHoverBg
                        },
                        '&.Mui-disabled': {
                            color: bluePalette.buttonAccentDisabledText,
                            borderColor: bluePalette.buttonAccentDisabledBorder,
                            backgroundColor: bluePalette.buttonAccentDisabledBg
                        }
                    },
                    '& .MuiDivider-root': {
                        borderColor: bluePalette.surfaceBorderSubtle
                    }
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: layoutTokens.borderRadiusPanel,
                    backdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    WebkitBackdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    border: easterComponentOverrides.cardBorder
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: bluePalette.contentPaperText,
                    backgroundColor: 'transparent',
                    padding: '16px 24px'
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    color: bluePalette.contentPaperText,
                    backgroundColor: 'transparent',
                    padding: '12px 24px 24px',
                    '&.MuiDialogContent-dividers': {
                        borderTop: `1px solid ${bluePalette.surfaceBorderSubtle}`,
                        borderBottom: 'none'
                    }
                }
            }
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    color: bluePalette.contentPaperText
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    borderTop: `1px solid ${bluePalette.surfaceBorderSubtle}`,
                    padding: '12px 24px',
                    minHeight: 64,
                    '& .MuiButton-root:not(.MuiButton-colorError)': {
                        color: bluePalette.buttonAccentBg,
                        border: `1px solid ${bluePalette.buttonAccentBg}`,
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '& .MuiSvgIcon-root': {
                            color: 'inherit'
                        },
                        '&:hover': {
                            backgroundColor: bluePalette.buttonAccentSoftBg,
                            borderColor: bluePalette.buttonAccentBg,
                            boxShadow: 'none'
                        },
                        '&.Mui-disabled': {
                            color: bluePalette.buttonAccentDisabledText,
                            borderColor: bluePalette.buttonAccentDisabledBorder,
                            backgroundColor: bluePalette.surfaceOverlayHover
                        }
                    }
                }
            }
        },
        MuiDataGrid: {
            defaultProps: {
                rowHeight: layoutTokens.tableRowHeight,
                columnHeaderHeight: layoutTokens.tableHeaderHeight
            },
            styleOverrides: {
                root: {
                    color: bluePalette.dateRangeText,
                    ...sharedDataGridRootStyle,
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                        borderBottom: `1px solid ${bluePalette.dataGridRowBorderColor}`
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'transparent',
                        borderBottom: `1px solid ${bluePalette.surfaceBorderSubtle}`,
                        boxShadow: 'none',
                        zIndex: 2
                    },
                    '& .MuiDataGrid-columnHeader': {
                        padding: `0 ${layoutTokens.tableCellHorizontalPadding}px`,
                        backgroundColor: 'transparent'
                    },
                    '& .MuiDataGrid-filler': {
                        backgroundColor: 'transparent'
                    },
                    '& .MuiDataGrid-topContainer': {
                        backgroundColor: bluePalette.surfaceOverlay,
                        boxShadow: 'none',
                        zIndex: 2
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        color: bluePalette.dataGridHeaderTitleColor,
                        fontWeight: 600
                    },
                    '& .MuiDataGrid-cell': {
                        padding: `0 ${layoutTokens.tableCellHorizontalPadding}px`,
                        borderColor: bluePalette.dataGridCellBorderColor
                    },
                    '& .MuiDataGrid-footerContainer': {
                        borderTop: `1px solid ${bluePalette.surfaceBorderSubtle}`,
                        backgroundColor: bluePalette.dataGridFooterBackground
                    },
                    '& .MuiDataGrid-columnSeparator': {
                        marginRight: '8px',
                        color: bluePalette.dataGridColumnSeparatorColor
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: bluePalette.surfaceOverlayHover
                    },
                    '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-iconButtonContainer button, & .MuiDataGrid-actionsCell .MuiIconButton-root':
                        {
                            color: `${bluePalette.buttonAccentBg} !important`
                        },
                    '& .MuiDataGrid-cell .MuiIconButton-root': {
                        color: `${bluePalette.buttonAccentBg} !important`
                    },
                    '& .MuiDataGrid-sortIcon': {
                        color: `${bluePalette.buttonAccentBg} !important`
                    },
                    '& .MuiTablePagination-root, & .MuiTablePagination-selectIcon': {
                        color: bluePalette.dataGridPaginationText
                    },
                    '& .MuiTablePagination-toolbar': {
                        color: bluePalette.dataGridPaginationText
                    },
                    '& .MuiTablePagination-select': {
                        color: bluePalette.contentPaperText,
                        borderRadius: `${layoutTokens.borderRadiusControl}px`,
                        backgroundColor: bluePalette.contentFilterInputBackground
                    },
                    '& .MuiTablePagination-select:hover': {
                        backgroundColor: bluePalette.surfaceOverlayHover
                    },
                    '& .MuiTablePagination-input .MuiOutlinedInput-notchedOutline, & .MuiTablePagination-input:before, & .MuiTablePagination-input:after':
                        {
                            borderColor: bluePalette.filterBorderColor
                        },
                    '& .MuiTablePagination-input:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiTablePagination-input.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: bluePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiTablePagination-actions .MuiSvgIcon-root': {
                        color: `${bluePalette.buttonAccentBg} !important`
                    },
                    '& .MuiTablePagination-selectIcon': {
                        color: `${bluePalette.buttonAccentBg} !important`
                    },
                    '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
                        color: bluePalette.dataGridPaginationText
                    },
                    '& .MuiDataGrid-overlay': {
                        backgroundColor: 'transparent'
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                        outline: 'none !important'
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                        outline: 'none !important'
                    },
                    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar':
                        {
                            height: 10,
                            width: 10
                        },
                    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-track':
                        {
                            background: bluePalette.dataGridScrollbarTrack,
                            borderRadius: 8
                        },
                    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb':
                        {
                            background: bluePalette.dataGridScrollbarThumb,
                            borderRadius: 8,
                            border: `2px solid ${bluePalette.dataGridScrollbarThumbBorder}`
                        }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                containedError: {
                    color: '#ffdbe0',
                    backgroundColor: 'rgba(255, 122, 134, 0.2)',
                    border: '1px solid rgba(255, 122, 134, 0.46)',
                    boxShadow: 'none',
                    '& .MuiSvgIcon-root': {
                        color: 'inherit'
                    },
                    '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: 'rgba(255, 122, 134, 0.28)',
                        borderColor: 'rgba(255, 122, 134, 0.6)'
                    }
                },
                outlinedError: {
                    color: '#ffb4be',
                    borderColor: 'rgba(255, 122, 134, 0.5)',
                    backgroundColor: 'rgba(255, 122, 134, 0.14)',
                    '& .MuiSvgIcon-root': {
                        color: 'inherit'
                    },
                    '&:hover': {
                        borderColor: 'rgba(255, 122, 134, 0.64)',
                        backgroundColor: 'rgba(255, 122, 134, 0.2)'
                    }
                },
                textError: {
                    color: '#ffb4be',
                    '& .MuiSvgIcon-root': {
                        color: 'inherit'
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(255, 122, 134, 0.12)'
                    }
                }
            }
        }
        // MuiAppBar: {
        //     styleOverrides: {
        //         root: {
        //             background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        //             boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        //         }
        //     }
        // },
        // MuiTab: {
        //     styleOverrides: {
        //         root: {
        //             color: '#ffffff',
        //             textTransform: 'uppercase',
        //             fontSize: '0.875rem',
        //             fontWeight: 600,
        //             letterSpacing: '0.5px',
        //             opacity: 0.85,
        //             transition: 'all 0.3s ease',
        //             '&:hover': {
        //                 opacity: 1,
        //                 backgroundColor: 'rgba(255, 255, 255, 0.05)'
        //             },
        //             '&.Mui-selected': {
        //                 color: '#ffffff',
        //                 opacity: 1,
        //                 borderBottom: '2px solid rgba(255, 255, 255, 0.9)',
        //                 fontWeight: 700
        //             }
        //         }
        //     }
        // },
        // MuiTabs: {
        //     styleOverrides: {
        //         indicator: {
        //             backgroundColor: 'rgba(255, 255, 255, 0.9)',
        //             height: 3
        //         },
        //         scrollButtons: {
        //             color: '#ffffff',
        //             opacity: 0.85,
        //             '&:hover': {
        //                 opacity: 1
        //             }
        //         }
        //     }
        // }
    }
});
